import React, { useState, useEffect } from 'react';
import { dataService } from '../../services/dataService';
import { Game, Standard } from '../../data/types';
import { usePermissions } from '../../contexts/PermissionContext';
import { RoleBasedButton } from '../common/RoleBasedComponent';
import PageContainer from '../layouts/PageContainer';
import TabContainer from '../layouts/TabContainer';
import ContentContainer from '../layouts/ContentContainer';

const SuperAdminGameManagementScreen: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'library' | 'import'>('library');
  
  // Data states
  const [games, setGames] = useState<Game[]>([]);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [loading, setLoading] = useState(true);
  // Upload status state commented out as it's not currently used
  // const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  
  // Filter states
  const [filterGrade, setFilterGrade] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Get user permissions - not currently used but kept for future use
  usePermissions();

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      const [gamesData, standardsData] = await Promise.all([
        dataService.getGames(),
        dataService.getStandards()
      ]);
      
      setGames(gamesData);
      setStandards(standardsData);
    } catch (error) {
      console.error('Error fetching game management data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload for game - implementation kept for future use
  /* 
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, gameData: Partial<Game>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadStatus('uploading');
      await dataService.uploadGame(gameData, file);
      setUploadStatus('success');
      
      // Refresh games list
      const gamesData = await dataService.getGames();
      setGames(gamesData);
      
      // Clear status after 3 seconds
      setTimeout(() => setUploadStatus('idle'), 3000);
    } catch (error) {
      console.error('Error uploading game:', error);
      setUploadStatus('error');
      
      // Clear status after 3 seconds
      setTimeout(() => setUploadStatus('idle'), 3000);
    }
  };
  */

  // Filter games based on criteria
  const filteredGames = games.filter(game => {
    // Filter by grade
    if (filterGrade !== 'all' && game.gradeLevel !== filterGrade) {
      return false;
    }
    
    // Filter by game type
    if (filterType !== 'all' && game.type !== filterType) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery && !game.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Get standard name by ID
  const getStandardName = (standardId: string) => {
    const standard = standards.find(s => s.id === standardId);
    return standard ? standard.code : 'Unknown';
  };

  // Get difficulty badge with appropriate color
  const getDifficultyBadge = (difficulty: string) => {
    let bgColor = '';
    let textColor = '';
    
    switch(difficulty) {
      case 'Easy':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Medium':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'Hard':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
    }
    
    return (
      <span className={`${bgColor} ${textColor} px-2 py-1 rounded-full text-xs font-medium`}>
        {difficulty}
      </span>
    );
  };

  // Render the Import Games tab content
  const renderImportTab = () => {
    return (
      <ContentContainer>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Import from CSV */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-4">Import Games from CSV</h3>
            <p className="text-sm text-gray-600 mb-6">
              Upload a CSV file containing game metadata to bulk import games into the system.
              Make sure your CSV follows the required format.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-center border-2 border-gray-300 border-dashed rounded-md px-6 pt-5 pb-6">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload-csv"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none"
                    >
                      <span>Upload a CSV file</span>
                      <input id="file-upload-csv" name="file-upload-csv" type="file" accept=".csv" className="sr-only" />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">CSV up to 10MB</p>
                </div>
              </div>
              
              <button
                type="button"
                className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary-dark"
              >
                Import Games
              </button>
            </div>
            
            <div className="mt-4">
              <button 
                className="text-sm text-primary hover:text-primary-dark flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Download CSV Template
              </button>
            </div>
          </div>
          
          {/* Manual Game Upload */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-4">Upload Individual Game</h3>
            <p className="text-sm text-gray-600 mb-6">
              Upload a game ZIP file containing the game assets and configuration.
              You'll need to provide basic metadata about the game.
            </p>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="game-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Game Title
                </label>
                <input
                  type="text"
                  id="game-title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter game title"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="game-grade" className="block text-sm font-medium text-gray-700 mb-1">
                    Grade Level
                  </label>
                  <select
                    id="game-grade"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Grade</option>
                    <option value="K">Kindergarten</option>
                    <option value="1">1st Grade</option>
                    <option value="2">2nd Grade</option>
                    <option value="3">3rd Grade</option>
                    <option value="4">4th Grade</option>
                    <option value="5">5th Grade</option>
                    <option value="6">6th Grade</option>
                    <option value="7">7th Grade</option>
                    <option value="8">8th Grade</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="game-difficulty" className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    id="game-difficulty"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Difficulty</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="game-file" className="block text-sm font-medium text-gray-700 mb-1">
                  Game File (ZIP)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload-zip"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none"
                      >
                        <span>Upload a ZIP file</span>
                        <input id="file-upload-zip" name="file-upload-zip" type="file" accept=".zip" className="sr-only" />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">ZIP up to 50MB</p>
                  </div>
                </div>
              </div>
              
              <button
                type="button"
                className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary-dark"
              >
                Upload Game
              </button>
            </div>
          </div>
        </div>
      </ContentContainer>
    );
  };

  // Render the Game Library tab content
  const renderLibraryTab = () => {
    return (
      <div>
        <div className="mb-6">
          <ContentContainer>
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="w-full md:w-64">
                <div className="relative">
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                    placeholder="Search games..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <svg
                    className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
              
              {/* Grade Filter */}
              <div className="w-full md:w-40">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={filterGrade}
                  onChange={(e) => setFilterGrade(e.target.value)}
                >
                  <option value="all">All Grades</option>
                  <option value="K">Kindergarten</option>
                  <option value="1">1st Grade</option>
                  <option value="2">2nd Grade</option>
                  <option value="3">3rd Grade</option>
                  <option value="4">4th Grade</option>
                  <option value="5">5th Grade</option>
                  <option value="6">6th Grade</option>
                  <option value="7">7th Grade</option>
                  <option value="8">8th Grade</option>
                </select>
              </div>
              
              {/* Type Filter */}
              <div className="w-full md:w-40">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="practice">Practice</option>
                  <option value="assessment">Assessment</option>
                  <option value="quiz">Quiz</option>
                  <option value="interactive">Interactive</option>
                </select>
              </div>
            </div>
          </ContentContainer>
        </div>
        
        <ContentContainer>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGames.map(game => (
                <div
                  key={game.id}
                  className={`border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200 ${selectedGame?.id === game.id ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setSelectedGame(game)}
                >
                  {/* Game Thumbnail */}
                  <div className="relative h-40 bg-gray-100">
                    {game.thumbnail ? (
                      <img
                        src={game.thumbnail}
                        alt={game.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                        </svg>
                      </div>
                    )}
                    
                    {/* Game Type Tag */}
                    <span className="absolute top-2 left-2 bg-primary text-white text-xs px-2 py-1 rounded-md">
                      {game.type}
                    </span>
                    
                    {/* Difficulty Badge */}
                    <div className="absolute top-2 right-2">
                      {getDifficultyBadge(game.difficulty)}
                    </div>
                  </div>
                  
                  {/* Game Info */}
                  <div className="p-4">
                    <h3 className="font-medium text-lg">{game.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{game.theme}</p>
                    
                    <div className="mt-3 flex items-center text-sm text-gray-600">
                      <span>Grade {game.gradeLevel}</span>
                      <span className="mx-2">•</span>
                      <span>{game.type}</span>
                    </div>
                    
                    {/* Standards */}
                    <div className="mt-3">
                      <h4 className="text-xs font-medium text-gray-700 mb-1">Standards:</h4>
                      <div className="flex flex-wrap gap-1">
                        {game.supportedStandards.map(stdId => (
                          <div key={stdId} className="flex items-center">
                            <span className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
                              {getStandardName(stdId)}
                            </span>
                            {game.primaryStandardId === stdId && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                Primary
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 border-t border-gray-100 bg-gray-50">
                    <h3 className="font-semibold text-gray-700 mb-2">Game Management</h3>
                    <div className="space-y-3">
                      {/* Preview Game - Available to all roles */}
                      <button className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary-dark flex items-center justify-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0110 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Preview Game
                      </button>
                      
                      {/* Edit Metadata - Only for SuperAdmin */}
                      <RoleBasedButton
                        resource="games"
                        requiredPermission="edit"
                        tooltip="Only Super Administrators can edit game metadata"
                        className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Edit Metadata
                      </RoleBasedButton>
                      
                      {/* Upload New Version - Only for SuperAdmin */}
                      <RoleBasedButton
                        resource="games"
                        requiredPermission="create"
                        tooltip="Only Super Administrators can upload new game versions"
                        className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload New Version
                      </RoleBasedButton>
                      
                      {/* Remove Game - Only for SuperAdmin */}
                      <RoleBasedButton
                        resource="games"
                        requiredPermission="delete"
                        tooltip="Only Super Administrators can remove games"
                        className="w-full bg-red-50 text-red-700 py-2 px-4 rounded hover:bg-red-100 flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove Game
                      </RoleBasedButton>
                    </div>
                  </div>
                </div>
              ))}
              
              {filteredGames.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                  <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900">No games found</h3>
                  <p className="text-gray-500 mt-1 max-w-md">
                    No games match your current filters. Try adjusting your search criteria or import new games.
                  </p>
                </div>
              )}
            </div>
          )}
        </ContentContainer>
      </div>
    );
  };

  return (
    <PageContainer title="Game Management" userRole="SuperAdmin">
      <TabContainer 
        tabContent={
          <div className="flex border-b border-b-transparent">
            <button
              className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'library' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('library')}
            >
              Game Library
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'import' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('import')}
            >
              Import Games
            </button>
          </div>
        }
      >
        {activeTab === 'library' ? renderLibraryTab() : renderImportTab()}
      </TabContainer>
    </PageContainer>
  );
};

export default SuperAdminGameManagementScreen;
